import { useFormik } from "formik"
import * as Yup from "yup"
import {
  Button,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap"
import Select from "react-select"
import { toast } from "react-toastify"

import FormInputField from "components/Common/FormInputField"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import VehicleIdSelect from "components/Common/VehicleIdSelect"
import { useAuth } from "hooks/authHook"
import { ACCESS } from "constants/accessPermissions"
import { useCreateLead, useUpdateLead } from "hooks/leadHook"

const titleCase = s =>
  String(s || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase())

const toLocalInput = value => {
  if (!value) return ""
  const d = new Date(value)
  if (isNaN(d.getTime())) return ""
  const pad = n => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const LeadFormModal = ({ isOpen, toggle, lead, meta = {} }) => {
  const isEdit = Boolean(lead)
  const { hasPermission } = useAuth()
  const canAssign = hasPermission(ACCESS.LEAD.ASSIGN)

  const createMutation = useCreateLead()
  const updateMutation = useUpdateLead()
  const mutation = isEdit ? updateMutation : createMutation

  const sourceOptions = (meta.sources ?? []).map(s => ({ value: s, label: titleCase(s) }))
  const priorityOptions = (meta.priorities ?? []).map(p => ({ value: p, label: titleCase(p) }))
  const assigneeOptions = (meta.assignees ?? []).map(e => ({ value: e.id, label: e.name }))

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: lead?.name ?? "",
      mobile: lead?.mobile ?? "",
      email: lead?.email ?? "",
      source: lead?.source ?? "walk_in",
      priority: lead?.priority ?? "medium",
      interested_in: lead?.interested_in ?? "",
      budget: lead?.budget ?? "",
      vehicle_id: lead?.vehicle_id ?? "",
      assigned_to: lead?.assigned_to ?? "",
      next_follow_up_at: toLocalInput(lead?.next_follow_up_at),
      notes: lead?.notes ?? "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Enter the lead name"),
      mobile: Yup.string()
        .required("Enter a contact number")
        .matches(/^01[3-9]\d{8}$/, "Enter an 11-digit mobile number, e.g. 01712345678"),
      email: Yup.string().email("Invalid email").nullable(),
      source: Yup.string().required("Choose a source"),
      budget: Yup.number().typeError("Numbers only").min(0).nullable(),
    }),
    onSubmit: async values => {
      const payload = {
        ...values,
        email: values.email || null,
        interested_in: values.interested_in || null,
        budget: values.budget === "" ? null : Number(values.budget),
        vehicle_id: values.vehicle_id || null,
        next_follow_up_at: values.next_follow_up_at || null,
        notes: values.notes || null,
      }

      if (isEdit) {
        delete payload.assigned_to
      } else if (!payload.assigned_to) {
        delete payload.assigned_to
      }

      try {
        if (isEdit) {
          await updateMutation.mutateAsync({ id: lead.id, ...payload })
          toast.success("Lead updated")
        } else {
          await createMutation.mutateAsync(payload)
          toast.success("Lead captured")
        }
        toggle()
      } catch (err) {
        toast.error(err?.response?.data?.message || "Save failed")
      }
    },
  })

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" scrollable>
      <ModalHeader toggle={toggle}>{isEdit ? "Edit lead" : "New lead"}</ModalHeader>
      <form onSubmit={formik.handleSubmit}>
        <ModalBody>
          <Row>
            <Col md={6} className="mb-3">
              <FormInputField
                label={<>Full name <span className="text-danger">*</span></>}
                name="name"
                placeholder="Lead's full name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && formik.errors.name}
              />
            </Col>
            <Col md={6} className="mb-3">
              <FormInputField
                label={<>Mobile number <span className="text-danger">*</span></>}
                name="mobile"
                inputMode="numeric"
                maxLength={11}
                placeholder="01712345678"
                value={formik.values.mobile}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.mobile && formik.errors.mobile}
              />
            </Col>
            <Col md={6} className="mb-3">
              <FormInputField
                label="Email address"
                name="email"
                type="email"
                placeholder="name@example.com"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && formik.errors.email}
              />
            </Col>
            <Col md={6}>
              <FormikCreatableSelect
                label="Lead source"
                name="source"
                formik={formik}
                options={sourceOptions}
                placeholder="Select a source"
                required
              />
            </Col>
            <Col md={6}>
              <FormikCreatableSelect
                label="Priority"
                name="priority"
                formik={formik}
                options={priorityOptions}
                placeholder="Select priority"
              />
            </Col>
            {!isEdit && canAssign && (
              <Col md={6} className="mb-3">
                <label className="form-label" htmlFor="assigned_to">
                  Assign to salesperson
                </label>
                <Select
                  inputId="assigned_to"
                  options={assigneeOptions}
                  value={assigneeOptions.find(o => o.value === formik.values.assigned_to) ?? null}
                  onChange={o => formik.setFieldValue("assigned_to", o?.value ?? "")}
                  placeholder="Unassigned"
                  isClearable
                />
              </Col>
            )}
            <Col md={6} className="mb-3">
              <FormInputField
                label="Interested in"
                name="interested_in"
                placeholder="e.g. Axio 2015, black"
                value={formik.values.interested_in}
                onChange={formik.handleChange}
              />
            </Col>
            <Col md={6} className="mb-3">
              <FormInputField
                label="Budget (BDT)"
                type="number"
                name="budget"
                placeholder="e.g. 1500000"
                value={formik.values.budget}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.budget && formik.errors.budget}
              />
            </Col>
            <Col md={6} className="mb-3">
              <label className="form-label" htmlFor="vehicle_id">
                Interested vehicle (stock)
              </label>
              <VehicleIdSelect formik={formik} isLabel={false} />
            </Col>
            <Col md={6} className="mb-3">
              <FormInputField
                label="Next follow-up"
                type="datetime-local"
                name="next_follow_up_at"
                value={formik.values.next_follow_up_at}
                onChange={formik.handleChange}
              />
            </Col>
            <Col md={12} className="mb-3">
              <FormInputField
                label="Notes"
                type="textarea"
                name="notes"
                placeholder="Any extra context about this lead"
                value={formik.values.notes}
                onChange={formik.handleChange}
              />
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" type="button" onClick={toggle}>
            Cancel
          </Button>
          <Button color="primary" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? "Saving..." : isEdit ? "Save" : "Capture lead"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}

export default LeadFormModal
